import { Component, OnInit } from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    Validators,
    AbstractControl,
} from '@angular/forms';
import { Empresa } from '../../interfaces/empresa.interface';
import { EmpresaService } from '../../services/empresa.service';
import { EditEmpresaService } from '../../services/edit-empresa.service';
import { ActivatedRoute, Router } from '@angular/router';
import { EmpresaHelperService } from '../../services/editar-empresa-helper.service';
import Swal from 'sweetalert2';
import { RegistroService } from 'src/app/registro.service';
import { firstValueFrom } from 'rxjs';
import { CargaEmpresasService } from 'src/app/services/carga-empresas.service';
import { KeycloakService } from 'keycloak-angular';
import { TranslateService } from '@ngx-translate/core';
import { etiquetaProducto } from '../../utils/etiquetas-producto.util';

type Cargo = { nombre: string };

@Component({
    selector: 'app-edit-empresa',
    templateUrl: './edit-empresa.component.html',
    styleUrls: ['./edit-empresa.component.css'],
})
export class EditEmpresaComponent implements OnInit {
    empresaForm!: FormGroup;

    empresa!: Empresa;
    departamentos: any[] = [];
    empresaActual: any;
    userId!: string;
    cargos: Cargo[] = [];
    readonly opcionesProducto: string[] = [
        'Alimentos balanceados',
        'Agroinsumos',
    ];
    etiquetaProducto = etiquetaProducto;
    opcionesEslabon: string[] = [
        'Productor',
        'Importador',
        'Comercializador',
        'Empacador',
        'Envasador',
    ];

    selectedProductos: string[] = [];
    selectedEslabon: string[] = [];
    eslabonOriginal: string[] = [];
    tieneProductos = false;

    constructor(
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private empresaService: EmpresaService,
        private editarService: EditEmpresaService,
        private empresaHelper: EmpresaHelperService,
        private router: Router,
        private registroService: RegistroService,
        private cargaEmpresasService: CargaEmpresasService,
        private keycloakService: KeycloakService,
        private translate: TranslateService
    ) {
        this.translate.setDefaultLang('es');
        this.translate.use('es');
        this.empresaService
            .getDepartamentos()
            .subscribe((departamento) => (this.departamentos = departamento));
        this.empresaService
            .getCargos()
            .subscribe((cargo) => (this.cargos = cargo));
    }

    ngOnInit(): void {
        this.route.params.subscribe((params) => {
            this.userId = params['id'];
        });

        this.empresaForm = this.fb.group({
            tipoEmpresa: ['', [Validators.required]],
            tipo_producto_que_importa: ['', [Validators.required]],
            eslabon: ['', [Validators.required]],
            nit: [{ value: '', disabled: true }],
            digito_verificacion: [
                '',
                [Validators.required, Validators.pattern(/^[0-9]{1}$/)],
            ],
            razonSocial: ['', [Validators.required]],
            responsableInformacion: ['', [Validators.required]],
            cargoDependencia: ['', [Validators.required]],
            telefono: [
                '',
                [Validators.required, Validators.pattern(/^[0-9]{10}$/)],
            ],
            celular: [''],
            correo_corporativo: ['', [Validators.required, Validators.email]],
        });

        this.getEmpresa();
        this.empresaForm.get('nit')?.disable({ emitEvent: false });
        this.empresaForm
            .get('digito_verificacion')
            ?.disable({ emitEvent: false });
    }

    get f() {
        return this.empresaForm.controls;
    }

    /**
     * Determina si debe **desviarse** el flujo a la pantalla de
     * caracterización de **Comercializador** (en construcción).
     *
     * Criterio:
     * - Retorna `true` cuando está seleccionado **Comercializador**
     *   y **no** hay seleccionados **Productor** ni **Importador**.
     *   (Es decir: solo Comercializador, o Comercializador con Empacador/Envasador).
     *
     * @returns {boolean} `true` si se debe navegar a la ruta de Comercializador; de lo contrario `false`.
     */
    private shouldNavigateToComercializador(): boolean {
        const sel = this.selectedEslabon || [];
        const tieneCom = sel.includes('Comercializador');
        const tieneProdOImp =
            sel.includes('Productor') || sel.includes('Importador');
        return tieneCom && !tieneProdOImp;
    }

    /**
     * Indica si un eslabón está **seleccionado** actualmente.
     *
     * @param {string} opcion Eslabón a consultar.
     * @returns {boolean} `true` si `opcion` existe en `selectedEslabon`; `false` en caso contrario.
     */
    isSelectedEslabon(opcion: string): boolean {
        return this.selectedEslabon.includes(opcion);
    }

    /**
     * Alterna la selección de un eslabón en el conjunto `selectedEslabon`
     * y sincroniza el `FormControl` reactivo `eslabon`.
     *
     * Comportamiento:
     * - Si el eslabón ya estaba seleccionado, lo quita.
     * - Si no lo estaba y no está deshabilitado, lo agrega.
     * - Actualiza el valor del control `eslabon` con el join por `'/'`
     *   y marca el control como `touched` para disparar validaciones de UI.
     *
     * @param {string} value  Eslabón a alternar (p.ej. 'Productor', 'Importador', 'Comercializador', 'Empacador', 'Envasador').
     * @param {Event}  $event Evento del clic; se usa `preventDefault()` para evitar envío de formulario o focos no deseados.
     * @returns {void}
     */
    toggleEslabon(value: string, $event: Event): void {
        $event.preventDefault();

        const validacion = this.validarCambioEslabon(value);
        if (!validacion.permitido) {
            Swal.fire('Cambio no permitido', validacion.mensaje, 'warning');
            return;
        }

        const activo = this.isSelectedEslabon(value);
        if (activo) {
            this.removeEslabon(value);
        } else {
            if (this.isEslabonDisabled(value)) return;
            this.addEslabon(value);
        }

        this.empresaForm
            .get('eslabon')
            ?.setValue(this.selectedEslabon.join('/'));
        this.empresaForm.get('eslabon')?.markAsTouched();
    }

    control(name: string): AbstractControl {
        return this.f[name];
    }

    /**
     * Indica si un control ha sido “tocado” para efectos de UI.
     * Considera **tocado** cuando el control está `touched` ,
     * para mostrar mensajes de validación apenas el usuario interactúe
     * o modifique el campo.
     * @param {string} name Nombre del control dentro del `FormGroup`.
     * @returns {boolean} `true` si el control está `touched` o `dirty`; de lo contrario `false`.
     */
    touched(name: string): boolean {
        const c = this.control(name);
        return !!(c.touched || c.dirty);
    }

    showError(name: string, errorKey: string): boolean {
        const c = this.control(name);
        return !!(c.hasError(errorKey) && this.touched(name));
    }

    async getEmpresa(): Promise<void> {
        try {
            this.empresaActual = await this.empresaHelper.getEmpresa(
                this.userId
            );

            if (this.empresaActual?.tipo_producto_que_importa) {
                this.selectedProductos =
                    this.empresaActual.tipo_producto_que_importa
                        .split(' y ')
                        .filter(Boolean);
            }

            if (this.empresaActual?.eslabon) {
                this.selectedEslabon = this.empresaActual.eslabon
                    .split('/')
                    .filter(Boolean);
                this.eslabonOriginal = [...this.selectedEslabon];
            }

            // Verificar si la empresa tiene productos registrados
            if (this.empresaActual?.id && this.empresaActual?.eslabon) {
                try {
                    const response: any = await firstValueFrom(
                        this.empresaService.empresaTieneProductos(
                            this.empresaActual.id,
                            this.empresaActual.eslabon
                        )
                    );

                    // Manejar diferentes estructuras de respuesta:
                    if (Array.isArray(response)) {
                        // Respuesta de productor/importador
                        this.tieneProductos = response.length > 0;
                    } else if (response?.data && Array.isArray(response.data)) {
                        // Respuesta de comercializador
                        this.tieneProductos = response.data.length > 0;
                    } else {
                        this.tieneProductos = false;
                    }
                } catch (error) {
                    console.error(
                        'Error al verificar productos de la empresa:',
                        error
                    );
                    this.tieneProductos = false;
                }
            }

            this.empresaForm.patchValue({
                ...this.empresaActual,
                tipoEmpresa: this.empresaActual?.tipoEmpresa || '',
                tipo_producto_que_importa: this.selectedProductos.join(' y '),
                eslabon: this.selectedEslabon.join('/'),
            });

            this.updateEslabonOptions();
        } catch (error) {
            console.error('Ocurrió un error obteniendo la información', error);
        }
    }

    /**
     * Alterna la selección de un **tipo de producto** en `selectedProductos`
     * y sincroniza el `FormControl` reactivo `tipo_producto_que_importa`.
     *
     * Previene el comportamiento por defecto del botón.
     * Si el producto ya está seleccionado → lo quita usando `EmpresaHelperService.removeProducto`.
     * De lo contrario → lo agrega con `EmpresaHelperService.addProducto`.
     * Actualiza el valor del control con los productos unidos por `' y '`
     * y marca el control como `touched` para disparar validaciones de UI.
     * Llama a `updateEslabonOptions()` para recalcular las opciones de eslabón
     * en función de los productos elegidos (p. ej., restringir cuando solo hay "Alimentos balanceados").
     *
     * @async
     * @param {string} value   Producto a alternar (p.ej. 'Alimentos balanceados', 'Agroinsumos').
     * @param {Event}  $event  Evento del clic; se usa `preventDefault()` para evitar envíos/foco indeseado.
     * @returns {Promise<void>} No retorna valor.
     */
    async toggleProducto(value: string, $event: Event): Promise<void> {
        $event.preventDefault();
        if (this.selectedProductos.includes(value)) {
            this.selectedProductos = await this.empresaHelper.removeProducto(
                value,
                this.empresaForm
            );
        } else {
            this.selectedProductos = await this.empresaHelper.addProducto(
                value,
                this.empresaForm
            );
        }

        this.empresaForm
            .get('tipo_producto_que_importa')
            ?.setValue(this.selectedProductos.join(' y '));
        this.empresaForm.get('tipo_producto_que_importa')?.markAsTouched();
        this.updateEslabonOptions();
    }

    /**
     * Agrega un eslabón a `selectedEslabon` si aún no está presente.     *
     * No aplica reglas de dependencia aquí; solo realiza la inserción única.
     * Las validaciones/ajustes (p. ej., limpiar dependientes) se manejan en otros métodos.     *
     * @param {string} value Eslabón a agregar (e.g., 'Productor', 'Importador', 'Comercializador', 'Empacador', 'Envasador').
     * @returns {void}
     */
    private addEslabon(value: string): void {
        if (!this.selectedEslabon.includes(value)) {
            this.selectedEslabon.push(value);
        }
    }

    /**
     * Quita un eslabón de `selectedEslabon` y aplica la regla de dependencia.
     * Reglas aplicadas:
     * - Si se elimina un eslabón **principal** (`Productor`, `Importador` o `Comercializador`)
     *   y ya **no queda** ningún principal seleccionado, se eliminan automáticamente los
     *   eslabones **dependientes**: `Empacador` y `Envasador`.
     *
     * @param {string} value Eslabón a remover.
     * @returns {void}
     */
    private removeEslabon(value: string): void {
        const index = this.selectedEslabon.indexOf(value);
        if (index > -1) this.selectedEslabon.splice(index, 1);

        if (['Productor', 'Importador', 'Comercializador'].includes(value)) {
            const principalSigue = this.selectedEslabon.some((v) =>
                ['Productor', 'Importador', 'Comercializador'].includes(v)
            );
            if (!principalSigue) {
                this.removeEslabonSiExiste('Empacador');
                this.removeEslabonSiExiste('Envasador');
            }
        }
    }

    /**
     * Elimina un eslabón **si está presente** en `selectedEslabon`.     *
     * Uso típico: limpiar eslabones dependientes (p. ej., quitar 'Empacador'/'Envasador'
     * cuando ya no hay eslabones principales seleccionados).
     *
     * @param {string} eslabon Nombre del eslabón a remover.
     * @returns {void}
     */
    private removeEslabonSiExiste(eslabon: string): void {
        const idx = this.selectedEslabon.indexOf(eslabon);
        if (idx > -1) this.selectedEslabon.splice(idx, 1);
    }

    /**
     * Determina si un eslabón debe estar **deshabilitado** en la UI.
     *
     * Reglas:
     * - Los eslabones **dependientes** (`Empacador`, `Envasador`) solo se habilitan
     *   cuando hay al menos un eslabón **principal** seleccionado:
     *   `Productor`, `Importador` o `Comercializador`.
     * - Si la empresa tiene productos:
     *   - Si era SOLO comercializador: deshabilitar cambio de eslabones principales
     *   - Si era productor/importador: deshabilitar si dejaría solo comercializador
     *
     * @param {string} opcion Eslabón a evaluar.
     * @returns {boolean} `true` si debe deshabilitarse; `false` en caso contrario.
     */
    isEslabonDisabled(opcion: string): boolean {
        // Regla base: Empacador/Envasador requieren un principal
        const requierePrincipal = ['Empacador', 'Envasador'].includes(opcion);
        if (requierePrincipal) {
            const tienePrincipal = this.selectedEslabon.some((v) =>
                ['Productor', 'Importador', 'Comercializador'].includes(v)
            );
            if (!tienePrincipal) return true;
        }

        if (this.tieneProductos) {
            const eslabonesPrincipales = [
                'Productor',
                'Importador',
                'Comercializador',
            ];
            const esEslabonPrincipal = eslabonesPrincipales.includes(opcion);

            if (
                this.eslabonOriginalEraSoloComercializador() &&
                esEslabonPrincipal
            ) {
                const yaSeleccionado = this.selectedEslabon.includes(opcion);
                const estaEnOriginal = this.eslabonOriginal.includes(opcion);
                return !estaEnOriginal || !yaSeleccionado;
            }
            if (
                this.eslabonOriginalTeniaProductorOImportador() &&
                esEslabonPrincipal
            ) {
                const yaSeleccionado = this.selectedEslabon.includes(opcion);
                if (!yaSeleccionado) return false;

                if (opcion === 'Productor' || opcion === 'Importador') {
                    const otrosPrincipales = this.selectedEslabon.filter(
                        (e) =>
                            (e === 'Productor' || e === 'Importador') &&
                            e !== opcion
                    );
                    const tieneCom =
                        this.selectedEslabon.includes('Comercializador');
                    if (otrosPrincipales.length === 0 && tieneCom) {
                        return true;
                    }
                }
            }
        }

        return false;
    }

    private updateEslabonOptions(): void {
        if (
            this.selectedProductos.length === 1 &&
            this.selectedProductos[0] === 'Alimentos balanceados'
        ) {
            this.opcionesEslabon = [
                'Productor',
                'Importador',
                'Comercializador',
            ];
            // Solo remover eslabones secundarios (Empacador/Envasador).
            // No remover Comercializador: si la empresa ya lo tiene asignado
            // debe conservarlo al editar.
            ['Empacador', 'Envasador'].forEach((e) =>
                this.removeEslabonSiExiste(e)
            );
            this.empresaForm
                .get('eslabon')
                ?.setValue(this.selectedEslabon.join('/'));
        } else {
            this.opcionesEslabon = [
                'Productor',
                'Importador',
                'Comercializador',
                'Empacador',
                'Envasador',
            ];
        }
    }

    private validarEslabonesSeleccionados(): boolean {
        const tieneEmpacadorOEnvasador = this.selectedEslabon.some((v) =>
            ['Empacador', 'Envasador'].includes(v)
        );
        const tienePrincipal = this.selectedEslabon.some((v) =>
            ['Productor', 'Importador', 'Comercializador'].includes(v)
        );
        return !(tieneEmpacadorOEnvasador && !tienePrincipal);
    }

    async putEmpresa(url: string | null): Promise<void> {
        try {
            const endpointId = `agente-vigilado/${this.empresaActual.id}`;
            await this.editarService.putEmpresaActual(
                endpointId,
                this.empresaForm.getRawValue()
            );

            this.registroService.listaEmpresasAsociadas = await firstValueFrom(
                this.cargaEmpresasService.obtenerEmpresas()
            );
            if(url){
                await Swal.fire('', 'Empresa actualizada correctamente', 'success');
            }

            const esUsuarioEditandoSuEmpresa = !this.userId;
            const esLaEmpresaSeleccionada =
                this.registroService.empresaSelected &&
                this.empresaActual.id.toString() ==
                this.registroService.empresaSelected;

            if (esUsuarioEditandoSuEmpresa || esLaEmpresaSeleccionada) {
                this.registroService.eslabonEmpresaSelected =
                    this.empresaForm.get('eslabon')?.value;

                // Forzar actualización de asociados para consistencia
                this.registroService.empresasAsociadas = true;
            }
            if(url){
                this.router.navigate([url]);
            }
        } catch (error) {
            console.error('Ocurrió un error al actualizar', error);
        }
    }

    /**
     * Convierte el texto de un campo de formulario a mayúsculas en tiempo real.
     *
     * Este método se enlaza al evento `(input)` de un elemento `<input>`.
     * Captura el valor actual, lo transforma a mayúsculas y actualiza el
     * `FormControl` correspondiente. Para mejorar la experiencia de usuario,
     * restaura la posición del cursor después de la transformación.
     * Utiliza `{ emitEvent: false }` para evitar disparar eventos `valueChanges`
     * y prevenir ciclos de actualización no deseados.
     *
     * @param {Event} event El evento de entrada (input) del DOM, que contiene el elemento target.
     * @param {string} controlName El nombre del control del formulario (`FormControlName`) que se va a actualizar.
     * @returns {void} No retorna ningún valor.
     */
    toUpperCase(event: Event, controlName: string) {
        const input = event.target as HTMLInputElement;
        const start = input.selectionStart;
        const end = input.selectionEnd;
        const value = input.value.toUpperCase();
        this.empresaForm.get(controlName).setValue(value, { emitEvent: false });
        input.setSelectionRange(start, end);
    }

    /**
     * @name submitForm
     * @description
     * Envía el formulario de edición de empresa aplicando validaciones, reglas de negocio
     * y flujo condicional de navegación para el eslabón **Comercializador**.
     *
     * ## Flujo
     * 1. Marca todos los controles como tocados para mostrar errores.
     * 2. Sincroniza el `FormControl` `eslabon` con los chips seleccionados.
     * 3. Valida la regla: si se elige Empacador/Envasador, debe existir Productor/Importador/Comercializador.
     * 4. Si el formulario es válido:
     *    - Si el eslabón es **solo Comercializador** o **Comercializador + (Empacador/Envasador)** *sin* Productor/Importador:
     *      muestra un SweetAlert y navega a la pantalla `/comercializador` (en construcción) con `getRawValue()`.
     *    - En cualquier otro caso: persiste normalmente mediante `putEmpresa()`.
     */
    async submitForm(): Promise<void> {
        this.empresaForm.markAllAsTouched();
        this.empresaForm
            .get('eslabon')
            ?.setValue(this.selectedEslabon.join('/'), { emitEvent: false });

        const productosOk = this.selectedProductos.length > 0;
        const eslabonOk = this.selectedEslabon.length > 0;

        if (!this.validarEslabonesSeleccionados()) {
            Swal.fire(
                'Error de validación',
                'Para seleccionar "Empacador" o "Envasador", también debe seleccionar "Productor", "Importador" o "Comercializador".',
                'error'
            );
            return;
        }

        // Validar reglas de negocio para empresas con productos
        if (this.tieneProductos) {
            const tieneProdOImp =
                this.selectedEslabon.includes('Productor') ||
                this.selectedEslabon.includes('Importador');
            const tieneCom = this.selectedEslabon.includes('Comercializador');
            if (
                this.eslabonOriginalTeniaProductorOImportador() &&
                tieneCom &&
                !tieneProdOImp
            ) {
                Swal.fire(
                    'Error de validación',
                    'No puede eliminar Productor/Importador dejando solo Comercializador en una empresa con productos registrados.',
                    'error'
                );
                return;
            }
            if (this.eslabonOriginalEraSoloComercializador()) {
                const eslabonesPrincipalesOriginales = this.eslabonOriginal
                    .filter((e) =>
                        ['Productor', 'Importador', 'Comercializador'].includes(
                            e
                        )
                    )
                    .sort();
                const eslabonesPrincipalesActuales = this.selectedEslabon
                    .filter((e) =>
                        ['Productor', 'Importador', 'Comercializador'].includes(
                            e
                        )
                    )
                    .sort();

                if (
                    eslabonesPrincipalesOriginales.join('/') !==
                    eslabonesPrincipalesActuales.join('/')
                ) {
                    Swal.fire(
                        'Error de validación',
                        'No puede modificar el eslabón principal de una empresa comercializadora con productos registrados.',
                        'error'
                    );
                    return;
                }
            }
        }

        const perfilUsuario = await this.keycloakService.loadUserProfile();
        const email = this.empresaForm.get('correo_corporativo').value;

        if (email == perfilUsuario.email) {
            this.translate
                .get('empresa.validarCorreo')
                .subscribe((translatedText: string) => {
                    Swal.fire({
                        title: 'Información',
                        text: translatedText,
                        icon: 'info',
                        confirmButtonText: 'Continuar',
                    });
                });
            return;
        }
        
        if (this.empresaForm.valid && productosOk && eslabonOk) {
            if (this.shouldNavigateToComercializador()) {
                await this.putEmpresa(null);
                const translatedText = await firstValueFrom(
                    this.translate.get(
                        'empresa.alertaCaracterizacionComercializador'
                    )
                );
                Swal.fire({
                    title: 'Información',
                    text: translatedText,
                    icon: 'info',
                    confirmButtonText: 'Continuar',
                }).then(() => {                  
                    this.router.navigate(['/comercializador'], {
                        state: {
                            empresaData: this.empresaForm.getRawValue(),
                            source: 'edit-empresa',
                            empresaId: this.empresaActual.id,
                            agente_vigilado_id: this.empresaActual.id,
                            isEditing: true,
                        },
                    });
                });
                return;
            } else {
                await this.putEmpresa('/inicio');
            }
        } else {
            this.empresaForm.markAllAsTouched();
            console.log('Ocurrió un error');
        }
    }

    /**
     * Determina si el eslabón original era SOLO comercializador (con o sin auxiliares)
     * @returns {boolean} true si era solo comercializador
     */
    private eslabonOriginalEraSoloComercializador(): boolean {
        const tieneCom = this.eslabonOriginal.includes('Comercializador');
        const tieneProdOImp =
            this.eslabonOriginal.includes('Productor') ||
            this.eslabonOriginal.includes('Importador');
        return tieneCom && !tieneProdOImp;
    }

    /**
     * Determina si el eslabón original incluía Productor o Importador
     * @returns {boolean} true si tenía productor o importador
     */
    private eslabonOriginalTeniaProductorOImportador(): boolean {
        return (
            this.eslabonOriginal.includes('Productor') ||
            this.eslabonOriginal.includes('Importador')
        );
    }

    /**
     * Valida si se puede modificar un eslabón específico según las reglas de negocio
     * @param {string} eslabon - El eslabón que se quiere modificar
     * @returns {object} { permitido: boolean, mensaje?: string }
     */
    private validarCambioEslabon(eslabon: string): {
        permitido: boolean;
        mensaje?: string;
    } {
        // Si no tiene productos, permitir cualquier cambio
        if (!this.tieneProductos) {
            return { permitido: true };
        }

        const eslabonesPrincipales = [
            'Productor',
            'Importador',
            'Comercializador',
        ];
        const esEslabonPrincipal = eslabonesPrincipales.includes(eslabon);
        if (
            this.eslabonOriginalEraSoloComercializador() &&
            esEslabonPrincipal
        ) {
            return {
                permitido: false,
                mensaje:
                    'No puede modificar el eslabón principal de una empresa comercializadora con productos registrados. Solo puede agregar o quitar Empacador/Envasador.',
            };
        }
        if (this.eslabonOriginalTeniaProductorOImportador()) {
            // Simular el estado después del cambio
            const estadoDespuesCambio = this.isSelectedEslabon(eslabon)
                ? this.selectedEslabon.filter((e) => e !== eslabon)
                : [...this.selectedEslabon, eslabon];

            const tieneProdOImp =
                estadoDespuesCambio.includes('Productor') ||
                estadoDespuesCambio.includes('Importador');
            const tieneCom = estadoDespuesCambio.includes('Comercializador');

            if (tieneCom && !tieneProdOImp && esEslabonPrincipal) {
                return {
                    permitido: false,
                    mensaje:
                        'No puede eliminar Productor/Importador dejando solo Comercializador en una empresa con productos registrados.',
                };
            }
        }

        return { permitido: true };
    }

    /**
     * Valida que la selección de eslabones cumpla con las reglas de negocio antes de guardar.
     *
     * @author: Rafael Salgado
     * @private
     * @returns {boolean} Retorna `true` si la combinación de eslabones es válida.
     * Retorna `false` si se ha seleccionado 'Empacador' o 'Envasador' sin 'Productor' o 'Importador',
     * lo que previene que el formulario se envíe.
     */
}
